import React, { useEffect, useRef, useState } from 'react';
import { Truck, Warehouse, PackageCheck, Handshake, TrendingUp, HandCoins, DollarSign, Settings, Radar, ChevronRight, ChevronLeft } from 'lucide-react';
import { MODULE_RAIL } from './moduleTabs';

const RAIL_ICONS = {
  purchasing: Truck,
  inventory: Warehouse,
  warehouse: PackageCheck,
  crm: Handshake,
  sales: TrendingUp,
  receivables: HandCoins,
  accounting: DollarSign,
  global: Settings,
  'market-intel': Radar,
};

// A hover→close gap this short reads as instant but still survives the
// pointer briefly leaving the wrap while crossing between columns.
const CLOSE_DELAY_MS = 200;

// Left-hand module switcher: an icon-only rail that replaces the old
// horizontal top tabs (those wrapped/scrolled once too many modules were
// added, especially on narrower screens). Hovering an icon cascades two
// flyout columns, straight off the same navigation data the full sidebar
// uses: column 1 lists that module's sections (Dashboard, Operations,
// Lists, ...), hovering a section slides out column 2 with its pages, and
// clicking a page navigates there. Clicking the icon itself still jumps
// straight to that module's default page — useful for touch/keyboard.
function ModuleRail({ activeModule, moduleNavigation, onSelectModule, onNavigate, sidebarCollapsed, onToggleSidebar, onExpandSidebar, navCounts = {} }) {
  const [openModuleKey, setOpenModuleKey] = useState(null);
  const [openSectionHeader, setOpenSectionHeader] = useState(null);
  const [openSectionTop, setOpenSectionTop] = useState(0);
  const railRef = useRef(null);
  const closeTimerRef = useRef(null);

  useEffect(() => {
    function handleOutsideClick(event) {
      if (railRef.current && !railRef.current.contains(event.target)) {
        setOpenModuleKey(null);
        setOpenSectionHeader(null);
      }
    }
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  useEffect(() => () => clearTimeout(closeTimerRef.current), []);

  // Fires on every mouseenter of the wrap — including when the pointer
  // bridges the small gap between column 1 and column 2, which briefly
  // passes over the page content behind them. Only reset which section is
  // open when a *different* module's icon is entered; otherwise the second
  // column would collapse out from under the pointer on its way there.
  const openModule = (key) => {
    clearTimeout(closeTimerRef.current);
    if (openModuleKey !== key) setOpenSectionHeader(null);
    setOpenModuleKey(key);
  };

  const openSection = (header, rowTop) => {
    clearTimeout(closeTimerRef.current);
    setOpenSectionHeader(header);
    setOpenSectionTop(rowTop);
  };

  const scheduleClose = () => {
    clearTimeout(closeTimerRef.current);
    closeTimerRef.current = setTimeout(() => {
      setOpenModuleKey(null);
      setOpenSectionHeader(null);
    }, CLOSE_DELAY_MS);
  };

  // Navigates to the module's default page and expands the full sidebar
  // panel to that module's sections — the rail's own hover flyout is just a
  // quick preview, so a click dismisses it rather than leaving it open.
  // Clicking the *already-active* (highlighted) icon instead just retracts
  // the sidebar, same as pressing the collapse toggle — you're already
  // there, so a second click reads as "put it away", not "go there again".
  const handleSelectModule = (moduleKey) => {
    clearTimeout(closeTimerRef.current);
    setOpenModuleKey(null);
    setOpenSectionHeader(null);
    if (moduleKey === activeModule) {
      onToggleSidebar();
      return;
    }
    onSelectModule(moduleKey);
    onExpandSidebar();
  };

  const handleSelectPage = (pageKey) => {
    clearTimeout(closeTimerRef.current);
    setOpenModuleKey(null);
    setOpenSectionHeader(null);
    onNavigate(pageKey);
  };

  return (
    <div className="module-rail" ref={railRef}>
      <button
        type="button"
        className="rail-icon-btn rail-toggle-btn"
        title={sidebarCollapsed ? 'Expand menu' : 'Collapse menu'}
        aria-label={sidebarCollapsed ? 'Expand menu' : 'Collapse menu'}
        onClick={onToggleSidebar}
      >
        {sidebarCollapsed ? <ChevronRight size={16} strokeWidth={2} /> : <ChevronLeft size={16} strokeWidth={2} />}
      </button>

      {/* A module with no screens the staff member can open (issue #252)
          has no sections left in moduleNavigation, so it isn't shown. */}
      {MODULE_RAIL.filter((entry) => (moduleNavigation[entry.moduleKey] || []).length).map((entry) => {
        const Icon = RAIL_ICONS[entry.key];
        const isActive = activeModule === entry.moduleKey;
        const isOpen = openModuleKey === entry.key;
        const sections = moduleNavigation[entry.moduleKey] || [];
        const openSection_ = isOpen ? sections.find((section) => section.header === openSectionHeader) : null;

        return (
          <div
            className="rail-item-wrap"
            key={entry.key}
            onMouseEnter={() => openModule(entry.key)}
            onMouseLeave={scheduleClose}
          >
            <button
              type="button"
              className={`rail-icon-btn ${isActive ? 'active' : ''} ${isOpen ? 'open' : ''}`}
              title={entry.label}
              aria-expanded={isOpen}
              onClick={() => handleSelectModule(entry.moduleKey)}
            >
              <Icon size={20} strokeWidth={1.75} />
            </button>

            {isOpen && (
              <div className="rail-flyout" role="menu">
                <div className="rail-flyout-title">{entry.label}</div>
                {sections.map((section) => {
                  // A section with only one page (Dashboard being the
                  // universal example) has nothing to cascade to — showing
                  // "Dashboard" as a row that opens a second column
                  // containing only "Dashboard" again is pure redundancy.
                  // Collapse it into one directly-clickable row instead.
                  if (section.items.length === 1) {
                    const onlyItem = section.items[0];
                    return (
                      <button
                        key={section.header}
                        type="button"
                        role="menuitem"
                        className="rail-flyout-item"
                        onClick={() => handleSelectPage(onlyItem.pageKey)}
                      >
                        {section.header}
                      </button>
                    );
                  }
                  return (
                    <button
                      key={section.header}
                      type="button"
                      className={`rail-flyout-row ${openSectionHeader === section.header ? 'open-parent' : ''}`}
                      onMouseEnter={(event) => openSection(section.header, event.currentTarget.offsetTop)}
                    >
                      <span>{section.header}</span>
                      <ChevronRight size={14} strokeWidth={2} className="rail-flyout-chev" />
                    </button>
                  );
                })}
              </div>
            )}

            {openSection_ && (
              <div className="rail-flyout rail-flyout-secondary" role="menu" style={{ top: openSectionTop }}>
                {openSection_.items.map((item) => (
                  <button
                    key={item.pageKey}
                    type="button"
                    role="menuitem"
                    className="rail-flyout-item"
                    onClick={() => handleSelectPage(item.pageKey)}
                  >
                    {item.label}
                    {navCounts[item.pageKey] > 0 && <span className="nav-count">{navCounts[item.pageKey]}</span>}
                  </button>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default ModuleRail;
